import React, { useState, useEffect, useContext, useRef } from "react";
import {
  ChannelContext,
  RegionContext,
  QualityContext,
  PlayerContext,
} from "../contexts";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import "../assets/styles/player.sass";
import Utility from "../Utility";
import PlayIcon from "../assets/images/direct/player-play.svg";
import PauseIcon from "../assets/images/direct/player-pause.svg";
import IcecastMetadataStats from "icecast-metadata-stats";
import { defaultRegionIndex, playerRecoveryPause } from "../constants";
var statsListener;
var inError = false;

async function isStreamReachable(url, timeoutMs = playerRecoveryPause * 1000) {
  // Default timeout of 5 seconds
  const controller = new AbortController(); // Create an AbortController
  const id = setTimeout(() => controller.abort(), timeoutMs); // Set a timeout to abort the fetch

  try {
    const response = await fetch(url, {
      method: "OPTIONS",
      signal: controller.signal,
      mode: "cors",
    });

    clearTimeout(id);

    if (response.ok) {
      console.log(
        `URL ${url} is reachable (OPTIONS method). Status: ${response.status}`
      );
      return true;
    } else {
      console.warn(
        `URL ${url} returned status ${response.status} (OPTIONS method).`
      );
      return false;
    }
  } catch (error) {
    clearTimeout(id);
    if (error.name === "AbortError") {
      console.error(
        `Error checking URL ${url}: Request timed out after ${timeoutMs}ms.`
      );
      return false;
    } else {
      console.error(`Error checking URL ${url}:`, error);

      return false;
    }
  }
}

const getUrl = ({
  content,
  currentChannel,
  regionIndexFromContext,
  mirror,
  qualityIndexFromContext,
}) => {
  const channel = content ? Utility.getChannel(content, currentChannel) : null;
  let regionIndex = regionIndexFromContext;
  if (
    channel &&
    channel.ether &&
    channel.ether.regions &&
    Array.isArray(channel.ether.regions) &&
    !channel.ether.regions[regionIndex]
  ) {
    regionIndex = 0;
    if (!channel.ether.regions[regionIndex]) return null;
  }

  let qualityIndex = qualityIndexFromContext;
  if (
    channel &&
    channel.ether &&
    channel.ether.regions &&
    Array.isArray(channel.ether.regions) &&
    channel.ether.regions[regionIndex].qualityList &&
    Array.isArray(channel.ether.regions[regionIndex].qualityList) &&
    !channel.ether.regions[regionIndex].qualityList[qualityIndex]
  ) {
    qualityIndex = 0;
    if (!channel.ether.regions[regionIndex].qualityList[qualityIndex])
      return null;
  }

  const url =
    channel &&
    channel.ether &&
    channel.ether.regions &&
    Array.isArray(channel.ether.regions) &&
    channel.ether.regions[regionIndex].qualityList &&
    Array.isArray(channel.ether.regions[regionIndex].qualityList) &&
    channel.ether.regions[regionIndex].qualityList[qualityIndex] &&
    channel.ether.regions[regionIndex].qualityList[qualityIndex].mirrors &&
    Array.isArray(
      channel.ether.regions[regionIndex].qualityList[qualityIndex].mirrors
    ) &&
    channel.ether.regions[regionIndex].qualityList[qualityIndex].mirrors
      .length > mirror
      ? channel.ether.regions[regionIndex].qualityList[qualityIndex].mirrors[
          mirror
        ].url
      : null;
  return url;
};

const Player = ({ content }) => {
  const [playerStyle, setPlayerStyle] = useState(null);
  const [wasPlaying, setWasPlaying] = useState(null);
  const [mirror, setMirror] = useState(0);
  const currentChannel = useContext(ChannelContext);
  const regionIndexFromContext = useContext(RegionContext);
  const { qualityIndex: qualityIndexFromContext } = useContext(QualityContext);
  const {
    currentUrl,
    setCurrentUrl,
    setReadyUrl,
    readyUrl,
    setPlayerRef,
    visible,
    showPlayer,
    playReadyUrl,
    userIntent,
    setMeta,
    meta,
  } = useContext(PlayerContext);
  const [isReachable, setIsReachable] = useState(false);
  const playerRef = useRef(null);

  const handlePlaybackError = () => {
    inError = true;
    setIsReachable(false);
    const isLastMirror = ({ mirror }) => {
      const url = getUrl({
        content: content,
        currentChannel: currentChannel,
        regionIndexFromContext: regionIndexFromContext,
        mirror: mirror + 1,
        qualityIndexFromContext: qualityIndexFromContext,
      }); // returns null if mirror not found
      if (url) return false;
      else return true;
    };
    if (wasPlaying != null) {
      //yes, wasPlaying != null
      if (wasPlaying == 0) {
        //yes, was playing first mirror
        console.log("yes, wasPlaying != null; yes, was playing first mirror");
        setWasPlaying(null);
        setMirror(mirror + 1);
      } else {
        //no, was NOT playing first mirror
        console.log(
          "yes, wasPlaying != null; no, was NOT playing first mirror"
        );
        setWasPlaying(null);
        setMirror(0);
      }
    } else {
      if (currentUrl.regionIndex == defaultRegionIndex) {
        // yes, default region
        if (isLastMirror({ mirror: mirror })) {
          //yes, last mirror
          console.log(
            "no, !(wasPlaying != null); yes, default region; yes, last mirror"
          );
          console.log("now with pause...");
          window.setTimeout(() => {
            console.log("pause ended");
            setMirror(0);
          }, playerRecoveryPause * 1000);
        } else {
          //no, there are more mirrors
          console.log(
            "no, !(wasPlaying != null); yes, default region; no, there are more mirrors"
          );
          setMirror(mirror + 1);
        }
      } else {
        // no, not default region
        if (isLastMirror({ mirror: mirror })) {
          //yes, last mirror
          console.log(
            "no, !(wasPlaying >= 0); no, not default region; yes, last mirror"
          );
          setMirror(0);
          setCurrentUrl({ ...currentUrl, regionIndex: defaultRegionIndex });
        } else {
          //no, there are more mirrors
          console.log(
            "no, !(wasPlaying >= 0); no, not default region; no, there are more mirrors"
          );
          setMirror(mirror + 1);
        }
      }
    }
  };
  useEffect(() => {
                  setMeta({
                channelId: currentChannel,
                title: null,
              });
    if (statsListener) statsListener.stop();
    if (currentUrl.url) {
      try {
        statsListener = new IcecastMetadataStats(currentUrl.url, {
          sources: ["icy"],
          onStats: (stats) => {
            if (stats && stats.icy && stats.icy.StreamTitle)
              setMeta({
                channelId: currentChannel,
                title: stats.icy.StreamTitle,
              });
          },
        });
        statsListener.start();
      } catch (error) {
        alert(error);
      }
    }
  }, [currentUrl]);
  useEffect(() => {
    setPlayerRef(playerRef);
  }, []);

  useEffect(() => {
    if (playerRef && playerRef.current && playerRef.current.audio) {
      try {
        playerRef.current.audio.current.play();
      } catch (error) {
        alert(error);
      }
    }
  }, [currentUrl]);

  useEffect(() => {
    const channel = content
      ? Utility.getChannel(content, currentChannel)
      : null;
    const style =
      channel && channel.colors && channel.colors.main
        ? { backgroundColor: channel.colors.main }
        : null;
    if (playerStyle !== style) {
      setPlayerStyle(style);
    }
  }, [currentChannel, content]);

  useEffect(() => {
    const url = getUrl({
      content: content,
      currentChannel: currentChannel,
      regionIndexFromContext: regionIndexFromContext,
      qualityIndexFromContext: qualityIndexFromContext,
      mirror: mirror,
    });
    if (url && (!readyUrl || readyUrl.url !== url)) {
      setReadyUrl({
        url: url,
        channelId: currentChannel,
        regionIndex: regionIndexFromContext,
      });
    }
  }, [
    content,
    currentChannel,
    regionIndexFromContext,
    qualityIndexFromContext,
  ]);

  useEffect(() => {
    const url = getUrl({
      content: content,
      currentChannel: currentChannel,
      regionIndexFromContext: currentUrl.regionIndex,
      qualityIndexFromContext: qualityIndexFromContext,
      mirror: mirror,
    });
    if (url && currentUrl.url !== url) {
      setCurrentUrl({
        ...currentUrl,
        url: url,
      });
    }
  }, [currentUrl, mirror]);

  useEffect(() => {
    if (userIntent) {
      showPlayer();
      playReadyUrl();
    }
  }, [qualityIndexFromContext]);
  useEffect(() => {
    if (userIntent) {
      showPlayer();
      playReadyUrl();
    }
  }, [regionIndexFromContext]);

  useEffect(() => {
    async function checkStream() {
      console.log("checking stream:", currentUrl.url);
      const reachable = await isStreamReachable(currentUrl.url);
      console.log("got result, reachable:" + reachable);
      if (reachable) {
        setIsReachable(reachable);
        inError = false;
        setWasPlaying(mirror);
      } else {
        setIsReachable(false);
        handlePlaybackError();
      }
    }
    if (currentUrl && currentUrl.url) checkStream();
  }, [currentUrl]);
  const result = (
    <div className="ur-mini-player" data-mirror={mirror}>
      <AudioPlayer
        onError={() => {
          if (!inError) {
            handlePlaybackError();
          }
        }}
        onPlayError={() => {
          if (!inError) {
            handlePlaybackError();
          }
        }}
        ref={playerRef}
        style={playerStyle ? playerStyle : null}
        autoPlayAfterSrcChange={true}
        customIcons={{
          play: <PlayIcon />,
          pause: <PauseIcon />,
        }}
        showJumpControls={false}
        showFilledVolume={true}
        src={isReachable ? currentUrl.url : null}
      />
      <div className="ur-mini-player-meta">
        {meta && meta.title ? meta.title : null}
      </div>
    </div>
  );
  return <>{visible ? result : null}</>;
};
export default Player;
